import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ClockIcon, ArrowRightIcon, SparklesIcon, HeartIcon, BoltIcon, CakeIcon, MoonIcon } from '@heroicons/react/24/outline';
import { getFeaturedHealthHacks, getAllHealthHacks, HealthHack } from '../services/api';
import NewsletterSignup from '../components/NewsletterSignup';

const HomePage: React.FC = () => {
  const [featuredHacks, setFeaturedHacks] = useState<HealthHack[]>([]);
  const [recentHacks, setRecentHacks] = useState<HealthHack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHacks = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch featured hacks
        const featured = await getFeaturedHealthHacks(6);
        setFeaturedHacks(featured);
        
        // Fetch all hacks for recent section (excluding featured ones)
        const allHacks = await getAllHealthHacks();
        const nonFeatured = allHacks.filter(hack => !hack.featured).slice(0, 6);
        setRecentHacks(nonFeatured);
        
      } catch (err) {
        setError('Failed to load daily tips');
        console.error('Error fetching daily tips:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHacks();
  }, []);

  const categories = [
    { id: 'stress-relief', name: 'Stress Relief', description: 'Practical techniques to reduce stress and anxiety', href: '/category/stress-relief' },
    { id: 'mindfulness', name: 'Mindfulness', description: 'Meditation and mindfulness practices', href: '/category/mindfulness' },
    { id: 'sleep', name: 'Sleep Better', description: 'Tips for better sleep and relaxation', href: '/category/sleep' },
    { id: 'self-care', name: 'Self Care', description: 'Self-care practices for mental wellness', href: '/category/self-care' },
    { id: 'wellness', name: 'Wellness', description: 'General wellness and lifestyle tips', href: '/category/wellness' }
  ];

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  const getCategoryIcon = (categoryId: string) => {
    const icons = {
      'stress-relief': <HeartIcon className="w-6 h-6" />,
      mindfulness: <SparklesIcon className="w-6 h-6" />,
      sleep: <MoonIcon className="w-6 h-6" />,
      'self-care': <CakeIcon className="w-6 h-6" />,
      wellness: <BoltIcon className="w-6 h-6" />,
    };
    return icons[categoryId as keyof typeof icons] || icons.wellness;
  };

  const getCategoryColorForCards = (category: string) => {
    // Use consistent blue for all categories
    return 'category-badge';
  };

  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-health-50 via-white to-primary-50 flex items-center justify-center">
          <div className="text-center">
            <div className="loading-spinner h-16 w-16 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 font-medium">Loading your daily health inspiration...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Destress Daily - Your #1 Source for Daily Destress Tips & Stress Relief</title>
        <meta name="description" content="Destress Daily delivers practical destress tips, stress relief strategies, and wellness advice daily. Learn how to destress effectively with evidence-based techniques." />
        <meta name="keywords" content="destress, destress daily, stress relief, daily destress tips, how to destress, stress management, wellness tips, mental health, mindfulness, stress reduction, destress techniques" />
        <meta property="og:title" content="Destress Daily - Your #1 Source for Daily Destress Tips & Stress Relief" />
        <meta property="og:description" content="Destress Daily delivers practical destress tips, stress relief strategies, and wellness advice daily. Learn how to destress effectively with evidence-based techniques." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://destress-daily.com/" />
        <meta property="og:image" content="https://destress-daily.com/logo.png" />
        <meta property="og:site_name" content="Destress Daily" />
        
        {/* Twitter Card Meta Tags */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:image" content="https://destress-daily.com/logo.png" />
      </Helmet>
      {/* Hero Section */}
      <section className="hero-gradient text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center fade-in">
            <div className="flex justify-center mb-6">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-4 floating">
                <HeartIcon className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Destress Daily
            </h1>
            <p className="text-xl md:text-2xl text-white text-opacity-90 max-w-4xl mx-auto mb-12 leading-relaxed">
              Your daily source for practical health tips, wellness hacks, and evidence-based advice to improve every aspect of your life.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/tips" className="btn-primary text-lg px-8 py-4 bg-white text-primary-600 hover:bg-gray-50">
                <SparklesIcon className="h-5 w-5 mr-2" />
                Explore Daily Tips
              </Link>
              <Link to="/about" className="btn-secondary text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-primary-600">
                Learn More About Us
              </Link>
            </div>
          </div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white bg-opacity-10 rounded-full floating" style={{ animationDelay: '0s' }}></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-white bg-opacity-10 rounded-full floating" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-20 w-12 h-12 bg-white bg-opacity-10 rounded-full floating" style={{ animationDelay: '2s' }}></div>
      </section>

      {/* Newsletter Signup */}
      <div className="relative -mt-12 z-20">
        <NewsletterSignup variant="hero" />
      </div>

      {/* Featured Daily Tips */}
      {featuredHacks.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 slide-in-left">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Featured Daily Tips
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover our most popular and effective health tips that have helped thousands improve their wellness.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredHacks.map((hack, index) => (
                <article key={hack.id} className="health-hack-card bounce-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="mb-4">
                    <span className={`category-badge ${getCategoryColorForCards(hack.category)}`}>
                      {getCategoryName(hack.category)}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                    <Link to={`/tip/${hack.slug}`}>
                      {hack.title}
                    </Link>
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {hack.excerpt}
                  </p>
                  
                  {/* Tags */}
                  {hack.tags && hack.tags.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {hack.tags.slice(0, 3).map((tag, index) => (
                          <Link
                            key={index}
                            to={`/tag/${encodeURIComponent(tag)}`}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 hover:bg-primary-100 hover:text-primary-700 transition-colors duration-200"
                          >
                            #{tag}
                          </Link>
                        ))}
                        {hack.tags.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500">
                            +{hack.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {hack.read_time} min read
                    </div>
                    <Link
                      to={`/tip/${hack.slug}`}
                      className="text-primary-600 font-medium hover:text-primary-700 transition-colors duration-200 flex items-center"
                    >
                      Read More 
                      <ArrowRightIcon className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 slide-in-right">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Explore Wellness Categories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find stress relief tips and wellness advice tailored to your specific needs and interests.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => {
              // Use consistent blue for all categories with lighter borders
              const bgColorClass = 'bg-primary-50';
              const textColorClass = 'text-primary-600';
              const borderColorClass = 'border-primary-100';
              const hoverBgClass = 'hover:bg-primary-100';

              return (
                <Link
                  key={category.id}
                  to={category.href}
                  className={`group block bg-white rounded-2xl shadow-sm border ${borderColorClass} p-8 transition-all duration-300 ${hoverBgClass} hover:shadow-lg hover:scale-105`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 ${bgColorClass} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <div className={textColorClass}>
                        {getCategoryIcon(category.id)}
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                      {category.name}
                    </h3>
                    
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {category.description}
                    </p>
                    
                    <div className={`inline-flex items-center text-sm font-semibold ${textColorClass} group-hover:translate-x-1 transition-transform duration-300`}>
                      Explore Category
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent Daily Tips */}
      {recentHacks.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-16">
              <div className="slide-in-left">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  Recent Daily Tips
                </h2>
                <p className="text-xl text-gray-600">
                  Stay updated with our latest health tips and wellness advice.
                </p>
              </div>
              <Link to="/tips" className="btn-primary slide-in-right">
                View All Tips
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentHacks.map((hack, index) => (
                <article key={hack.id} className="health-hack-card fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="mb-4">
                    <span className={`category-badge ${getCategoryColorForCards(hack.category)}`}>
                      {getCategoryName(hack.category)}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                    <Link to={`/tip/${hack.slug}`}>
                      {hack.title}
                    </Link>
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {hack.excerpt}
                  </p>
                  
                  {/* Tags */}
                  {hack.tags && hack.tags.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {hack.tags.slice(0, 3).map((tag, index) => (
                          <Link
                            key={index}
                            to={`/tag/${encodeURIComponent(tag)}`}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 hover:bg-primary-100 hover:text-primary-700 transition-colors duration-200"
                          >
                            #{tag}
                          </Link>
                        ))}
                        {hack.tags.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500">
                            +{hack.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {hack.read_time} min read
                    </div>
                    <Link
                      to={`/tip/${hack.slug}`}
                      className="text-primary-600 font-medium hover:text-primary-700 transition-colors duration-200 flex items-center"
                    >
                      Read More 
                      <ArrowRightIcon className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Error Message */}
      {error && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-2xl mx-auto">
                <h3 className="text-2xl font-bold text-red-600 mb-4">Error Loading Daily Tips</h3>
                <p className="text-gray-600 mb-8">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Final Newsletter Signup */}
      <section className="py-12 bg-gradient-to-br from-primary-50 to-health-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <NewsletterSignup />
        </div>
      </section>
    </>
  );
};

export default HomePage; 